'use client'

import { useEffect, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { IPhoneMockup } from '@/components/ui/iphone-mockup'
import { LiquidGlassCard } from '@/components/ui/liquid-glass-card'
import { Bell, Share, Plus, ChevronDown, X, TrendingUp, TrendingDown } from 'lucide-react'

const INCOMING = [
  { symbol: 'BNBUSDT',  dir: 'LONG',  type: 'Breakout signal',   price: '$594.10' },
  { symbol: 'OPUSDT',   dir: 'LONG',  type: 'CVD divergence',    price: '$1.312'  },
  { symbol: 'DOTUSDT',  dir: 'SHORT', type: 'Short setup',       price: '$6.18'   },
  { symbol: 'ARBUSDT',  dir: 'LONG',  type: 'EMA golden cross',  price: '$0.724'  },
  { symbol: 'SUIUSDT',  dir: 'LONG',  type: 'Breakout signal',   price: '$2.872'  },
]

const INITIAL_NOTIFICATIONS = [
  { symbol: 'BTCUSDT',  type: 'Breakout long',    price: '$83,412.00', time: '01:42', id: 'i0' },
  { symbol: 'SOLUSDT',  type: 'CVD divergence',    price: '$134.21',    time: '01:38', id: 'i1' },
  { symbol: 'ETHUSDT',  type: 'Short setup',       price: '$2,184.50',  time: '01:35', id: 'i2' },
  { symbol: 'AVAXUSDT', type: 'EMA golden cross',  price: '$28.74',     time: '01:29', id: 'i3' },
  { symbol: 'SUIUSDT',  type: 'Breakout long',     price: '$2.872',     time: '01:21', id: 'i4' },
  { symbol: 'LINKUSDT', type: 'CVD divergence',    price: '$13.42',     time: '01:14', id: 'i5' },
  { symbol: 'INJUSDT',  type: 'Support bounce',    price: '$18.55',     time: '01:07', id: 'i6' },
]

function delay(ms: number) {
  return new Promise<void>(r => setTimeout(r, ms))
}

function AppIcon({ size = 'md' }: { size?: 'sm' | 'md' }) {
  const dim = size === 'sm' ? 'h-8 w-8' : 'h-10 w-10'
  const img = size === 'sm' ? 'h-5 w-5' : 'h-6 w-6'
  return (
    <div className={`${dim} shrink-0 flex items-center justify-center rounded-xl overflow-hidden`}
      style={{ background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)', border: '1px solid rgba(255,255,255,0.1)' }}>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img src="/images/plane-takeoff.svg" alt="Algoglide" className={img}
        style={{ filter: 'brightness(0) saturate(100%) invert(70%) sepia(80%) saturate(600%) hue-rotate(5deg)' }} />
    </div>
  )
}

type Notification = { symbol: string; type: string; price: string; time: string; id: string }

function IosNotificationScreen() {
  const [phase, setPhase] = useState<'idle' | 'island' | 'expanded' | 'exit'>('idle')
  const [incomingIdx, setIncomingIdx] = useState(0)
  const [notifications, setNotifications] = useState<Notification[]>(INITIAL_NOTIFICATIONS)
  const idCounter = useRef(0)
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return
    started.current = true

    let cancelled = false
    let i = 0

    const run = async () => {
      await delay(600)
      while (!cancelled) {
        const idx = i % INCOMING.length
        const signal = INCOMING[idx]
        setIncomingIdx(idx)

        // island pill appears
        setPhase('island')
        await delay(800)
        if (cancelled) break

        // pill expands
        setPhase('expanded')
        await delay(3200)
        if (cancelled) break

        // add notification to top of list
        const now = new Date()
        const timeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
        const id = `live-${idCounter.current++}`
        setNotifications(prev => [
          { symbol: signal.symbol, type: signal.type, price: signal.price, time: timeStr, id },
          ...prev.slice(0, 8),
        ])

        // pill shrinks away
        setPhase('exit')
        await delay(600)
        if (cancelled) break

        setPhase('idle')
        await delay(1400)
        if (cancelled) break

        i++
      }
    }

    run()
    return () => { cancelled = true }
  }, [])

  const n = INCOMING[incomingIdx]
  const isLong = n.dir === 'LONG'

  return (
    <div className="relative h-full w-full overflow-hidden">
      {/* Wallpaper */}
      <div className="absolute inset-0"
        style={{ backgroundImage: 'url(/previews/yosemite.webp)', backgroundSize: 'cover', backgroundPosition: 'center', filter: 'brightness(0.7)' }} />

      {/* Custom dynamic island */}
      <div className="absolute top-2 left-0 right-0 flex justify-center pointer-events-none" style={{ zIndex: 10 }}>
        <AnimatePresence mode="wait">
          {phase === 'island' && (
            <motion.div
              key="island"
              initial={{ width: 120, height: 34, borderRadius: 20, opacity: 0 }}
              animate={{ width: 120, height: 34, borderRadius: 20, opacity: 1 }}
              exit={{ width: 120, height: 34, borderRadius: 20, opacity: 0 }}
              transition={{ duration: 0.25 }}
              style={{ background: '#000' }}
            />
          )}
          {phase === 'expanded' && (
            <motion.div
              key="expanded"
              initial={{ width: 120, height: 34, borderRadius: 20, opacity: 0.7 }}
              animate={{ width: 296, height: 70, borderRadius: 26, opacity: 1 }}
              exit={{ width: 120, height: 34, borderRadius: 20, opacity: 0 }}
              transition={{ duration: 0.45, ease: [0.34, 1.56, 0.64, 1] }}
              style={{ background: '#111', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.6)' }}
            >
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.25, duration: 0.2 }}
                className="flex items-center gap-3 px-3 h-full"
              >
                <AppIcon size="sm" />
                <div className="flex-1 min-w-0">
                  <div className="text-white text-xs font-semibold truncate">
                    {n.symbol.replace('USDT', '')} — {n.type}
                  </div>
                  <div className="text-white/50 text-[10px]">Algoglide · {n.price}</div>
                </div>
                <div className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold shrink-0"
                  style={{
                    background: isLong ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                    color: isLong ? '#4ade80' : '#f87171',
                  }}>
                  {isLong ? <TrendingUp className="size-3" /> : <TrendingDown className="size-3" />}
                  {n.dir}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Status bar */}
      <div className="relative flex items-center justify-between mx-3 px-4 pt-3 pb-1" style={{ zIndex: 5 }}>
        <span className="text-white text-sm font-semibold">11:25</span>
        <div className="flex items-center gap-1 text-white text-xs">
          <span>●●●</span>
          <span className="ml-1">LTE</span>
          <span className="ml-1 bg-[#30d158] text-black text-[9px] font-bold px-1 rounded">82</span>
        </div>
      </div>

      {/* Group header */}
      <div className="relative flex items-center justify-between mx-3 px-3 py-2" style={{ zIndex: 5 }}>
        <span className="text-white text-sm font-semibold">Algoglide</span>
        <div className="flex items-center gap-2">
          <button className="flex items-center gap-1 bg-white/20 rounded-full px-2.5 py-0.5 text-white text-xs">
            <ChevronDown className="size-3" /> Show less
          </button>
          <button className="flex items-center justify-center bg-white/20 rounded-full h-5 w-5 text-white">
            <X className="size-3" />
          </button>
        </div>
      </div>

      {/* Notification list */}
      <div className="relative px-4 space-y-1.5 overflow-y-auto" style={{ zIndex: 5, maxHeight: 'calc(100% - 100px)' }}>
        <AnimatePresence initial={false} mode="popLayout">
          {notifications.map((notif) => (
            <motion.div
              key={notif.id}
              layout="position"
              initial={{ opacity: 0, y: -28, scaleY: 0.88, originY: 0 }}
              animate={{ opacity: 1, y: 0, scaleY: 1 }}
              exit={{ opacity: 0, y: -8, scaleY: 0.94, originY: 0 }}
              transition={{
                layout: { type: 'spring', stiffness: 280, damping: 30 },
                opacity: { duration: 0.55, ease: [0.4, 0, 0.2, 1] },
                y: { type: 'spring', stiffness: 340, damping: 28 },
                scaleY: { type: 'spring', stiffness: 340, damping: 28 },
              }}
            >
              <LiquidGlassCard
                draggable={false}
                borderRadius="14px"
                blurIntensity="lg"
                shadowIntensity="sm"
                glowIntensity="xs"
                className="w-full"
              >
                <div className="flex items-center gap-2.5 px-3 py-2.5">
                  <AppIcon />
                  <div className="flex-1 min-w-0">
                    <div className="text-white text-xs font-semibold truncate">
                      {notif.symbol.replace('USDT', '')} — {notif.type}
                    </div>
                    <div className="text-white/60 text-[10px]">from Algoglide</div>
                    <div className="text-white/50 text-[10px]">Setup at {notif.price}</div>
                  </div>
                  <span className="text-white/40 text-[10px] shrink-0">{notif.time}</span>
                </div>
              </LiquidGlassCard>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Bottom dock */}
      <div className="absolute bottom-4 left-0 right-0 flex items-center justify-around px-8" style={{ zIndex: 5 }}>
        <LiquidGlassCard draggable={false} borderRadius="50%" blurIntensity="md" shadowIntensity="xs" glowIntensity="none" className="h-11 w-11">
          <div className="flex items-center justify-center h-11 w-11">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/images/ios-flashlight-1.svg" alt="Flashlight" className="size-6" style={{ filter: 'brightness(0) invert(1)' }} />
          </div>
        </LiquidGlassCard>

        <div className="h-11 w-11 flex items-center justify-center rounded-full">
          <span className="text-white text-[9px] font-medium">Personal</span>
        </div>

        <LiquidGlassCard draggable={false} borderRadius="50%" blurIntensity="md" shadowIntensity="xs" glowIntensity="none" className="h-11 w-11">
          <div className="flex items-center justify-center h-11 w-11">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/images/ios-camera.svg" alt="Camera" className="size-6" style={{ filter: 'brightness(0) invert(1)' }} />
          </div>
        </LiquidGlassCard>
      </div>
    </div>
  )
}

export function PwaSection() {
  return (
    <section
      className="bg-black py-16 md:py-32 overflow-hidden"
      style={{ fontFamily: 'var(--font-ppmori, PPMori), sans-serif' }}
    >
      <div className="mx-auto max-w-5xl px-6">
        <div className="grid gap-12 md:grid-cols-2 md:items-center">

          {/* Text — first on mobile, right on desktop */}
          <div className="space-y-6 md:order-2">
            <div className="space-y-3">
              <p className="text-sm uppercase tracking-widest text-white/30">No App Store. No download.</p>
              <h2 className="text-3xl font-medium text-white md:text-4xl lg:text-5xl">
                Install in seconds. Get signals forever.
              </h2>
            </div>
            <p className="text-base text-white/50 md:text-lg">
              Algoglide is a Progressive Web App — add it to your home screen directly from Safari or Chrome and it works exactly like a native app. No review process, no 200MB download, no account required beyond your browser.
            </p>
            <p className="text-sm text-white/50 md:text-base">
              Once installed, enable push notifications and every signal the scanner detects lands on your lock screen the moment it fires — whether you have the app open or not. Three steps:{' '}
              <span className="inline-flex items-center gap-1 text-white font-medium"><Share className="size-3 text-[#ffb703]" /> tap Share,</span>{' '}
              <span className="inline-flex items-center gap-1 text-white font-medium"><Plus className="size-3 text-[#ffb703]" /> Add to Home Screen,</span>{' '}
              <span className="inline-flex items-center gap-1 text-white font-medium"><Bell className="size-3 text-[#ffb703]" /> Allow Notifications.</span>{' '}
              That&apos;s it.
            </p>
          </div>

          {/* iPhone mockup — second on mobile, left on desktop */}
          <div className="w-full overflow-hidden flex justify-center md:order-1">
            {/* scale=1 so the component applies no transform; we control it here
                with transformOrigin: top center so flex justify-center works correctly.
                Negative marginBottom removes the dead layout space below the scaled element. */}
            <div style={{
              display: 'inline-block',
              transform: 'scale(0.72)',
              transformOrigin: 'top center',
              marginBottom: `${-(876 * 0.28)}px`,
            }}>
              <IPhoneMockup
                model="15-pro"
                color="black"
                scale={1}
                safeArea={false}
                showDynamicIsland={false}
                shadow="0 32px 80px rgba(0,0,0,0.7), 0 4px 12px rgba(0,0,0,0.4)"
              >
                <IosNotificationScreen />
              </IPhoneMockup>
            </div>
          </div>

        </div>
      </div>
    </section>
  )
}
